import { useCallback } from "react";

import { Text, Button, CheckboxGroup, Flex } from "@radix-ui/themes";
import { styled } from "@stitches/react";
import { useForm } from "react-hook-form";

import { ESTIMATED_ANNUAL_USAGE_OPTIONS } from "./constants";
import {
  ContactSupplierRequest,
  ContactSupplierRequestType,
} from "../../api/public";
import { SelectWithController } from "../../common/components";
import Textarea from "../../common/components/Textarea";

const FormContainer = styled(Flex, {
  "> fieldset": {
    border: "none",
    padding: 0,
    margin: 0,
  },
  '* label[data-required="true"]': {
    "&::after": {
      content: '"*"',
      color: "var(--red-11)",
    },
  },
});

type Props = {
  rawMaterialId?: string;
  onSubmit: (values: ContactSupplierRequest) => void;
  errors?: Record<string, string>;
};

export const ContactSupplierForm = ({ rawMaterialId, onSubmit }: Props) => {
  const {
    watch,
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    setValue,
  } = useForm<ContactSupplierRequest>({
    mode: "all",
    defaultValues: {
      raw_material_id: rawMaterialId,
      request_types: [],
      request: undefined,
      project_description: undefined,
      estimated_annual_usage: undefined,
    },
  });

  const requestTypes = watch("request_types");
  const handleRequestTypesChange = useCallback(
    (value: ContactSupplierRequestType) => {
      if (requestTypes.includes(value)) {
        setValue(
          "request_types",
          requestTypes.filter((v) => v !== value)
        );
      } else {
        setValue("request_types", [...requestTypes, value]);
      }
    },
    [requestTypes, setValue]
  );

  const hasErrors = Object.keys(errors).length > 0 || requestTypes.length === 0;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormContainer gap="3" direction="column">
        <fieldset>
          <Flex gap="1" direction="column">
            <Text as="label" color="gray" size="1" data-required>
              Request Type
            </Text>
            <CheckboxGroup.Root orientation="horizontal">
              <Flex gap="3">
                <CheckboxGroup.Item
                  value={ContactSupplierRequestType.SALES}
                  onClick={() =>
                    handleRequestTypesChange(ContactSupplierRequestType.SALES)
                  }
                >
                  Sales
                </CheckboxGroup.Item>
                <CheckboxGroup.Item
                  value={ContactSupplierRequestType.TECHNICAL}
                  onClick={() =>
                    handleRequestTypesChange(
                      ContactSupplierRequestType.TECHNICAL
                    )
                  }
                >
                  Technical
                </CheckboxGroup.Item>
              </Flex>
            </CheckboxGroup.Root>
          </Flex>
        </fieldset>
        <fieldset>
          <Text as="label" color="gray" size="1" data-required>
            Project Description
          </Text>
          <Textarea
            resize="vertical"
            placeholder="Please provide a brief description of your project"
            {...register("project_description", {
              required: true,
              minLength: 1,
            })}
          />
        </fieldset>
        <fieldset>
          <Text as="label" color="gray" size="1" data-required>
            Request
          </Text>
          <Textarea
            resize="vertical"
            placeholder="Please describe the details of your request"
            {...register("request", { required: true, minLength: 1 })}
          />
        </fieldset>
        <fieldset>
          <Text as="label" color="gray" size="1" data-required>
            Estimated Annual Usage
          </Text>
          <SelectWithController
            name="estimated_annual_usage"
            control={control}
            options={ESTIMATED_ANNUAL_USAGE_OPTIONS}
            isMulti={false}
            required={true}
            menuPosition="fixed"
          />
        </fieldset>
        <Button type="submit" disabled={(hasErrors && isDirty) || isSubmitting}>
          Submit
        </Button>
      </FormContainer>
    </form>
  );
};
